import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceTrendChart = ({ trendData }) => {
  const [chartType, setChartType] = useState('line');
  const [dateRange, setDateRange] = useState('all');

  const filterDataByRange = (data) => {
    if (dateRange === 'all') return data;
    
    const now = new Date();
    const filtered = data?.filter(item => {
      const itemDate = new Date(item.date);
      const daysDiff = Math.floor((now - itemDate) / (1000 * 60 * 60 * 24));
      
      if (dateRange === '7days') return daysDiff <= 7;
      if (dateRange === '30days') return daysDiff <= 30;
      if (dateRange === '90days') return daysDiff <= 90;
      return true;
    });
    
    return filtered;
  };

  const filteredData = filterDataByRange(trendData);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-elevation-2">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              <span style={{ color: entry?.color }}>{entry?.name}:</span> {entry?.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-elevation-2 animate-fade-in">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            Performance Trends
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Score progression over time
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
            <Button
              variant={dateRange === '7days' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateRange('7days')}
            >
              7 Days
            </Button>
            <Button
              variant={dateRange === '30days' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateRange('30days')}
            >
              30 Days
            </Button>
            <Button
              variant={dateRange === '90days' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateRange('90days')}
            >
              90 Days
            </Button>
            <Button
              variant={dateRange === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDateRange('all')}
            >
              All Time
            </Button>
          </div>

          <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
            <Button
              variant={chartType === 'line' ? 'default' : 'ghost'}
              size="sm"
              iconName="TrendingUp"
              onClick={() => setChartType('line')}
            >
              Line
            </Button>
            <Button
              variant={chartType === 'bar' ? 'default' : 'ghost'}
              size="sm"
              iconName="BarChart3"
              onClick={() => setChartType('bar')}
            >
              Bar
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-64 md:h-80 lg:h-96" aria-label="Performance Trend Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={filteredData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line 
                type="monotone" 
                dataKey="score" 
                stroke="var(--color-primary)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-primary)', r: 4 }}
                activeDot={{ r: 6 }}
                name="Overall Score"
              />
              <Line 
                type="monotone" 
                dataKey="average" 
                stroke="var(--color-secondary)" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: 'var(--color-secondary)', r: 3 }}
                name="Average Score"
              />
            </LineChart>
          ) : (
            <BarChart data={filteredData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar 
                dataKey="score" 
                fill="var(--color-primary)" 
                radius={[8, 8, 0, 0]}
                name="Overall Score"
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-success" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Highest Score</p>
            <p className="text-lg font-bold text-foreground data-text">
              {Math.max(...filteredData?.map(d => d?.score))}%
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Icon name="Activity" size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Average Score</p>
            <p className="text-lg font-bold text-foreground data-text">
              {Math.round(filteredData?.reduce((acc, d) => acc + d?.score, 0) / filteredData?.length)}%
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
            <Icon name="Target" size={20} className="text-accent" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Tests Taken</p>
            <p className="text-lg font-bold text-foreground data-text">
              {filteredData?.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrendChart;